import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  createCollectionRefund,
  voidCollectionRefund,
} from '#/api/crmMgmt/customerOrder/collection';
import { AppDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  allocationEffectiveAmount,
  buildRefundPayload,
  creditEffectiveAmount,
  localDateTimeToApi,
  nowLocalDateTime,
  receiptAvailableAmount,
  validateRefund,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import useCollectionMutation from './useCollectionMutation';

function ReversalRow({ id, label, expected, value, onChange }) {
  return (
    <div className="grid grid-cols-[1fr_9rem] items-center gap-2">
      <span className="text-sm">
        {label}
        <span className="ml-2 text-xs text-slate-500">目前 {formatDisplayAmount(expected)}</span>
      </span>
      <FloatingInput
        id={id}
        type="number"
        label="減少"
        min={0}
        step="0.01"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        suppressInlineError
      />
    </div>
  );
}

ReversalRow.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  expected: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * 登錄退款（REFUND）：真的把錢退給客戶。需逐筆指定要減少的期別抵付與子單分配，
 * 未分配的部分可不帶反向列。
 */
export const CollectionRefundDialog = ({
  open,
  onOpenChange,
  customerOrderId,
  receipts,
  allocations,
  summaryPhases,
  salesOrders,
}) => {
  const confirmed = receipts.filter((receipt) => receipt.status === 'CONFIRMED');
  const [receiptId, setReceiptId] = useState(confirmed[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [occurredAt, setOccurredAt] = useState(nowLocalDateTime);
  const [reason, setReason] = useState('');
  const [phaseValues, setPhaseValues] = useState({});
  const [salesValues, setSalesValues] = useState({});
  const [formError, setFormError] = useState(null);

  const receipt = confirmed.find((row) => row.id === receiptId);
  const phaseNoById = new Map(summaryPhases.map((phase) => [phase.phaseId, phase.phaseNo]));
  const soNoById = new Map(salesOrders.map((so) => [so.id, so.salesOrderNo]));

  const phaseInputs = (receipt?.credits ?? []).map((credit) => ({
    phaseId: credit.phaseId,
    expected: creditEffectiveAmount(credit),
    amount: phaseValues[credit.phaseId] ?? '',
  }));
  const salesInputs = allocations
    .filter((allocation) => allocation.receiptId === receiptId)
    .map((allocation) => ({
      allocationId: allocation.id,
      salesOrderId: allocation.salesOrderId,
      expected: allocationEffectiveAmount(allocation),
      amount: salesValues[allocation.id] ?? '',
    }));

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => createCollectionRefund(customerOrderId, payload, { idempotencyKey }),
    { successMessage: '已登錄退款', onSuccess: () => onOpenChange(false) },
  );

  function handleConfirm() {
    const actualOccurredAt = localDateTimeToApi(occurredAt);
    let error;
    if (!receipt) {
      error = '請選擇來源收款';
    } else if (!reason.trim()) {
      error = '請填寫退款原因';
    } else if (!actualOccurredAt) {
      error = '請填寫實際退款時間';
    } else {
      error = validateRefund({ receipt, amount, phaseInputs, salesInputs });
    }
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate(buildRefundPayload({
      receipt,
      amount,
      actualOccurredAt,
      reason: reason.trim(),
      phaseInputs,
      salesInputs,
    }));
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="登錄退款"
      description="實際退錢給客戶時使用；若子單因此分配不足，會從 PAID 退回 INVOICED。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '送出中…' : '登錄退款'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <SelectCore
          id="refund-receipt"
          label="來源收款"
          options={confirmed.map((row) => ({
            value: row.id,
            label: `${formatDateTimeOrDash(row.actualReceivedAt)}`
              + `・可用 ${formatDisplayAmount(receiptAvailableAmount(row))}`,
          }))}
          value={receiptId}
          onChange={(value) => {
            setReceiptId(value);
            setPhaseValues({});
            setSalesValues({});
          }}
        />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <FloatingInput
            id="refund-amount"
            type="number"
            label="退款金額"
            min={0}
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <FloatingInput
            id="refund-occurred-at"
            type="datetime-local"
            label="實際退款時間"
            value={occurredAt}
            onChange={(event) => setOccurredAt(event.target.value)}
          />
        </div>
        {phaseInputs.length > 0 ? <p className="text-sm text-slate-600">減少期別抵付</p> : null}
        {phaseInputs.map((row) => (
          <ReversalRow
            key={row.phaseId}
            id={`refund-phase-${row.phaseId}`}
            label={`第 ${phaseNoById.get(row.phaseId) ?? '?'} 期`}
            expected={row.expected}
            value={row.amount}
            onChange={(value) => setPhaseValues((prev) => ({ ...prev, [row.phaseId]: value }))}
          />
        ))}
        {salesInputs.length > 0 ? <p className="text-sm text-slate-600">減少子單分配</p> : null}
        {salesInputs.map((row) => (
          <ReversalRow
            key={row.allocationId}
            id={`refund-sales-${row.allocationId}`}
            label={soNoById.get(row.salesOrderId) ?? row.salesOrderId}
            expected={row.expected}
            value={row.amount}
            onChange={(value) => setSalesValues((prev) => ({ ...prev, [row.allocationId]: value }))}
          />
        ))}
        <FloatingTextarea
          id="refund-reason"
          label="退款原因"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionRefundDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  receipts: PropTypes.arrayOf(PropTypes.object).isRequired,
  allocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  summaryPhases: PropTypes.arrayOf(PropTypes.object).isRequired,
  salesOrders: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/** 退款沖銷（REFUND_VOID）：更正登錄錯誤的退款，恢復其期別與子單用途。 */
export const CollectionRefundVoidDialog = ({ open, onOpenChange, customerOrderId, refund }) => {
  const [occurredAt, setOccurredAt] = useState(nowLocalDateTime);
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState(null);

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => voidCollectionRefund(customerOrderId, refund.id, payload, { idempotencyKey }),
    { successMessage: '已沖銷退款', onSuccess: () => onOpenChange(false) },
  );

  function handleConfirm() {
    const actualOccurredAt = localDateTimeToApi(occurredAt);
    const error = !reason.trim() ? '請填寫原因' : (!actualOccurredAt ? '請填寫時間' : null);
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate({ actualOccurredAt, reason: reason.trim() });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="沖銷退款"
      description={`退款 ${formatDisplayAmount(refund.amount)}。來源收款已沖銷、期別容量不足或子單已取消時會被拒絕。`}
      headerVariant="muted"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '沖銷中…' : '沖銷退款'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <FloatingInput
          id="refund-void-occurred-at"
          type="datetime-local"
          label="沖銷時間"
          value={occurredAt}
          onChange={(event) => setOccurredAt(event.target.value)}
        />
        <FloatingTextarea
          id="refund-void-reason"
          label="原因"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionRefundVoidDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  refund: PropTypes.object.isRequired,
};
